import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { meQueryOptions, useSignup } from '~/api/auth';
import { formError } from '~/api/client';
import { AuthCard } from '~/app/AuthCard';
import { Button } from '~/ui/Button';
import { Input } from '~/ui/Input';
import { PasswordInput } from '~/ui/PasswordInput';
import { Notice } from '~/ui/Notice';

export const Route = createFileRoute('/signup')({
  beforeLoad: async ({ context }) => {
    // Already signed in: there is nothing to set up.
    const me = await context.queryClient.fetchQuery(meQueryOptions).catch(() => null);
    if (me) throw redirect({ to: '/dashboard' });
  },
  component: SignupPage,
});

const MIN = 10;

/** A new company and its owner in one step. The owner chooses their own password. */
function SignupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signup = useSignup();
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const longEnough = password.length >= MIN;
  const canSubmit =
    companyName.trim().length >= 2 && name.trim().length >= 2 && email.includes('@') && longEnough;
  const { fields: fieldErrors, message: formMessage } = formError(signup.error);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await signup.mutateAsync({
        companyName: companyName.trim(),
        name: name.trim(),
        email: email.trim(),
        password,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
      });
      await qc.fetchQuery(meQueryOptions);
      await navigate({ to: '/dashboard' });
    } catch {
      // shown below
    }
  };

  return (
    <AuthCard
      title="Set up your company."
      subtitle="You become its owner. Invite everyone else from inside."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Company name"
          name="organization"
          autoComplete="organization"
          required
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          error={fieldErrors['companyName']}
        />
        <Input
          label="Your name"
          name="name"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors['name']}
        />
        <Input
          label="Work email"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors['email']}
        />
        <PasswordInput
          label="Password"
          name="new-password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors['password']}
        />
        <p
          className={`flex items-center gap-1.5 text-xs ${longEnough ? 'text-done' : 'text-ink-3'}`}
        >
          <Check size={14} aria-hidden /> At least {MIN} characters
        </p>
        {formMessage && <Notice tone="late">{formMessage}</Notice>}
        <Button type="submit" block loading={signup.isPending} disabled={!canSubmit}>
          Create the company
        </Button>
      </form>
      <p className="mt-6 text-sm text-ink-3">
        Already have an account?{' '}
        <Link to="/login" className="text-accent hover:underline">
          Sign in
        </Link>
        . Joining a company that is already here? Open the invite link it sent you.
      </p>
    </AuthCard>
  );
}
